/**
 * Gmail thread-loader + auto-expand helpers extracted verbatim from
 * `ContactEditModal.tsx` (the heaviest effect in the file).
 *
 * The orchestrator KEEPS the `useEffect` shells (and their exact dependency
 * arrays + interval/ref lifecycle wiring); only the inner `fetchEmails` async
 * body and the `tryExpand` reader move here, behind typed deps. Behavior is
 * unchanged (#3293 Pass-2):
 *   - queries /contact-threads with ALL the contact's known addresses;
 *   - fetches every thread body in parallel via /thread;
 *   - drops calendar invites + automated mail and requires contact involvement;
 *   - `tryExpand` opens the latest message of a target thread if loaded.
 */

import { getAuthItem } from '@/lib/authStorage';
import { emailApiBase } from '@/lib/email-api';

export type GmailAttachment = { filename: string; mimeType: string; size?: number; attachmentId: string };

export interface GmailMessage {
  id: string;
  from: string;
  to: string;
  cc?: string;
  date: string;
  body: string;
  subject: string;
  threadId: string;
  attachments?: GmailAttachment[];
}

export interface FetchGmailThreadsDeps {
  email: string;
  contact: { emails?: string[]; attributes?: Record<string, any> } | null | undefined;
  gmailLoadAll: boolean;
  profileEmail: string | undefined;
  gmailAccountEmails: string[];
  setGmailSearching: (b: boolean) => void;
  setGmailMessages: (m: GmailMessage[]) => void;
}

/** Body of the contact-email Gmail thread-loader effect (`fetchEmails`). */
export async function fetchGmailThreads(deps: FetchGmailThreadsDeps): Promise<void> {
  const { email, contact, gmailLoadAll, profileEmail, gmailAccountEmails, setGmailSearching, setGmailMessages } = deps;

  const gmailHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
  const authToken = getAuthItem('xavior_auth_token');
  if (authToken) gmailHeaders['Authorization'] = `Bearer ${authToken}`;

  // Use ALL the contact's known addresses, not just the primary. A
  // contact often has personal + work emails on file (e.g.
  // maggie.nemser@gmail.com + maggie@blackboardeats.com); querying
  // only the primary missed an entire branch of the thread history.
  const allContactEmails = Array.from(new Set([
    email,
    ...(Array.isArray(contact?.emails) ? contact.emails : []),
    // attributes.emails are canonical `{ value }[]` objects (#3756) — map to the
    // value or the `typeof === 'string'` filter below drops every synced
    // address, narrowing the thread search to just the primary (the exact
    // missed-history bug this block exists to avoid).
    ...(Array.isArray((contact as any)?.attributes?.emails)
      ? ((contact as any).attributes.emails as unknown[]).map((e) =>
          typeof e === 'string' ? e : (e as { value?: string } | null)?.value || '',
        )
      : []),
  ].filter((e): e is string => typeof e === 'string' && e.length > 0).map(e => e.toLowerCase())));

  try {
    setGmailSearching(true);
    // DB-backed thread list (same shape as /search). Gmail's live API
    // is no longer hit on the 30s polling interval — the sync
    // service keeps gmail_messages fresh, and per-thread bodies are
    // still fetched below via /thread (cache-first 5 min).
    const newerThanIso = gmailLoadAll
      ? undefined
      : new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
    const searchRes = await fetch(`${emailApiBase()}/contact-threads`, {
      method: 'POST',
      headers: gmailHeaders,
      body: JSON.stringify({ email, emails: allContactEmails, max: gmailLoadAll ? 50 : 20, newerThan: newerThanIso }),
    });
    if (!searchRes.ok) { setGmailSearching(false); return; }
    const searchData = await searchRes.json();
    const threads: Array<{ id: string }> = searchData.threads || [];

    const allMessages: GmailMessage[] = [];
    const contactEmail = email.toLowerCase();
    // Filter messages by ANY of the contact's known addresses, not
    // just the primary — same rationale as the contact-threads fetch
    // above. Without this, threads pulled in via a secondary address
    // get re-dropped by the per-message filter below.
    const contactEmailSet = new Set(allContactEmails);
    const userEmails = [...new Set([profileEmail, ...gmailAccountEmails].filter(Boolean))].map(e => e!.toLowerCase());
    // Fetch all threads in parallel for speed
    const threadResults = await Promise.allSettled(
      threads.map(async (thread) => {
        const threadRes = await fetch(`${emailApiBase()}/thread`, {
          method: 'POST',
          headers: gmailHeaders,
          body: JSON.stringify({ threadId: thread.id }),
        });
        if (!threadRes.ok) return null;
        const threadData = await threadRes.json();
        return { threadId: thread.id, messages: threadData.messages || [] };
      })
    );
    // Collapse cross-account duplicates: the same email forwarded into / delivered
    // to more than one connected mailbox is synced once per account (different
    // per-account thread + message ids) and would otherwise render twice. Dedupe by
    // a content signature (from|subject|body).
    const seenKeys = new Set<string>();
    for (const result of threadResults) {
      if (result.status !== 'fulfilled' || !result.value) continue;
      const { threadId, messages } = result.value;
      for (const msg of messages) {
          // Filter out calendar invites and automated emails
          const msgBody = msg.body || msg.snippet || '';
          const msgSubject = msg.subject || '';
          const msgFrom = (msg.from || '').toLowerCase();
          const msgTo   = (msg.to  || '').toLowerCase();
          const msgCc   = (msg.cc  || '').toLowerCase();
          if (msgFrom.includes('calendar-notification@google.com')) continue;
          if (/^(Invitation|Updated invitation):/i.test(msgSubject)) continue;
          if (msgBody.includes('calendar.google.com') || msgBody.includes('schema.org/Event')) continue;
          // Require a DIRECT user↔contact pairing: the message must be either
          //   (a) sent FROM the contact TO the user, or
          //   (b) sent FROM the user TO the contact.
          // Checking CC as well catches contacts who received via CC.
          // This filters out group/class emails where the contact is merely a
          // co-recipient of a third-party sender (#3485).
          const recipients = msgTo + ' ' + msgCc;
          const fromIsContact = Array.from(contactEmailSet).some(e => msgFrom.includes(e));
          const toIsContact   = Array.from(contactEmailSet).some(e => recipients.includes(e));
          const fromIsUser    = userEmails.some(e => msgFrom.includes(e));
          const toIsUser      = userEmails.some(e => recipients.includes(e));
          const isDirect = (fromIsContact && toIsUser) || (fromIsUser && toIsContact);
          // The backend /contact-threads query already restricts results to
          // direct user↔contact threads (#3485). Re-applying the strict pairing
          // here with the FRONTEND's userEmails (profile + connected accounts)
          // silently dropped the entire conversation whenever that list was
          // missing the account actually on the mail (e.g. the connected
          // frk314@gmail.com vs. the profile's malibukrueger@gmail.com). Within
          // an already-direct thread, keep any message involving the contact.
          if (!isDirect && !(fromIsContact || toIsContact)) continue;

          // De-dup across accounts by a content signature. (Message-ID would be the
          // natural key, but forwarding nulls it on the forwarded copy, so
          // from|subject|body is the reliable cross-copy key. Date is excluded —
          // each account stamps its own received time.)
          const dedupKey = `${msgFrom}|${msgSubject}|${(msgBody || '').replace(/\s+/g, ' ').trim().slice(0, 200)}`;
          if (seenKeys.has(dedupKey)) continue;
          seenKeys.add(dedupKey);

          allMessages.push({
            id: msg.id || `${threadId}-${allMessages.length}`,
            from: msgFrom,
            to: msg.to || '',
            cc: msg.cc || '',
            date: msg.date || msg.internalDate || '',
            body: msgBody,
            subject: msgSubject,
            threadId,
            attachments: Array.isArray(msg.attachments) && msg.attachments.length > 0 ? msg.attachments : undefined,
          });
      }
    }
    setGmailMessages(allMessages);
  } catch { /* Gmail fetch failed silently */ }
  setGmailSearching(false);
}

export interface TryExpandDeps {
  gmailMessages: GmailMessage[];
  setExpandedEmail: (m: GmailMessage) => void;
}

/** Open the latest message of `tid` if it's already loaded. Returns success. */
export function tryExpandThread(tid: string, deps: TryExpandDeps): boolean {
  const { gmailMessages, setExpandedEmail } = deps;
  const matching = gmailMessages.filter(m => (m as any).threadId === tid);
  if (matching.length === 0) return false;
  const latest = matching.reduce((a, b) => (new Date(a.date).getTime() > new Date(b.date).getTime() ? a : b));
  setExpandedEmail(latest);
  return true;
}
